import { Button, Dialog, Field } from '@noorden-insurtech/alba-design-system';
import { Lock } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { FilterCombobox } from '@/features/reports/components/shared/filter-combobox.component';
import type {
  PolicyBatchFacets,
  PolicyBatchItem,
  PolicyBatchPatch,
} from '@/services/integrations';
import { POLICY_BATCH_STATUS_OPTIONS } from '@/shared/enums/policy-batch-status.enum';
import {
  buildDraftFromItem,
  buildPatchFromDraft,
  isEmptyPatch,
  policyBatchEditSchema,
  type PolicyBatchEditDraft,
} from '../schemas/policy-batch-edit.schema';
import {
  buildBatchIdOptions,
  buildChannelCodeOptions,
  formatLobOption,
  withCurrentOption,
} from '../utils/policy-batch-options.util';

interface PolicyBatchEditModalProps {
  item: PolicyBatchItem | null;
  facets?: PolicyBatchFacets;
  onClose: () => void;
  /** Não grava direto: a página abre a confirmação por digitação com este patch. */
  onRequestSave: (patch: PolicyBatchPatch) => void;
}

/** Campo somente leitura da chave do registro (PK/SK do DynamoDB). */
function LockedField({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) {
  return (
    <Field.Root>
      <Field.Label className='flex items-center gap-1'>
        {label}
        <Lock className='h-3 w-3 text-alba-gray-500' />
      </Field.Label>
      <Field.Input value={value} readOnly disabled />
    </Field.Root>
  );
}

/**
 * Edição de um número do balde.
 *
 * `Nº Apólice` e `Ramo` aparecem travados: são a chave da tabela no DynamoDB e chave não
 * se atualiza — trocá-los é excluir e reimportar.
 */
export function PolicyBatchEditModal({
  item,
  facets,
  onClose,
  onRequestSave,
}: PolicyBatchEditModalProps) {
  const [draft, setDraft] = useState<PolicyBatchEditDraft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setDraft(item ? buildDraftFromItem(item) : null);
    setErrors({});
  }, [item]);

  const channelCodeOptions = useMemo(
    () => buildChannelCodeOptions(facets),
    [facets]
  );
  const batchIdOptions = useMemo(() => buildBatchIdOptions(facets), [facets]);

  const update = (field: keyof PolicyBatchEditDraft, value: string) =>
    setDraft(previous =>
      previous ? { ...previous, [field]: value } : previous
    );

  const handleSave = () => {
    if (!item || !draft) return;

    const parsed = policyBatchEditSchema.safeParse(draft);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      parsed.error.issues.forEach(issue => {
        const field = String(issue.path[0] ?? '');
        if (field && !fieldErrors[field]) fieldErrors[field] = issue.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    onRequestSave(buildPatchFromDraft(item, parsed.data));
  };

  const patch = item && draft ? buildPatchFromDraft(item, draft) : {};
  const hasChanges = !isEmptyPatch(patch);

  return (
    <Dialog.Root
      open={Boolean(item)}
      onOpenChange={details => {
        if (!details.open) onClose();
      }}
    >
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content className='w-[620px] max-w-[92vw]'>
          <Dialog.Header>
            <Dialog.Title>Editar número do balde</Dialog.Title>
          </Dialog.Header>

          <Dialog.Body className='max-h-[65vh] space-y-4 overflow-y-auto'>
            {item && draft && (
              <>
                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                  <LockedField label='Nº Apólice' value={item.policyNumber} />
                  <LockedField
                    label='Ramo'
                    value={formatLobOption(item.lobId)}
                  />
                </div>
                <p className='-mt-2 text-xs text-alba-gray-500'>
                  Nº Apólice e Ramo identificam o registro e não podem ser
                  alterados. Para trocá-los, exclua este número e reimporte.
                </p>

                <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                  <Field.Root>
                    <Field.Label>Origem Emissão</Field.Label>
                    <FilterCombobox
                      placeholder='Selecione'
                      options={withCurrentOption(
                        channelCodeOptions,
                        draft.channelCode
                      )}
                      value={draft.channelCode}
                      onChange={value => update('channelCode', value)}
                    />
                    {errors.channelCode && (
                      <Field.ErrorText>{errors.channelCode}</Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Nº Balde</Field.Label>
                    <FilterCombobox
                      placeholder='Selecione'
                      options={withCurrentOption(batchIdOptions, draft.batchId)}
                      value={draft.batchId}
                      onChange={value => update('batchId', value)}
                    />
                    <Field.HelperText>
                      Mudar o balde pode mover este número para o balde de outro
                      produto.
                    </Field.HelperText>
                    {errors.batchId && (
                      <Field.ErrorText>{errors.batchId}</Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Status</Field.Label>
                    <FilterCombobox
                      placeholder='Selecione'
                      options={POLICY_BATCH_STATUS_OPTIONS}
                      value={draft.status}
                      onChange={value => value && update('status', value)}
                    />
                    <Field.HelperText>
                      Liberar o número consulta o i4Pro antes de salvar.
                    </Field.HelperText>
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>ID Solicitação</Field.Label>
                    <Field.Input
                      type='number'
                      inputMode='numeric'
                      value={draft.requestId}
                      onChange={event =>
                        update('requestId', event.target.value)
                      }
                      onKeyDown={event => event.stopPropagation()}
                    />
                    {errors.requestId && (
                      <Field.ErrorText>{errors.requestId}</Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Data Balde</Field.Label>
                    <Field.Input
                      type='datetime-local'
                      value={draft.batchDate}
                      onChange={event =>
                        update('batchDate', event.target.value)
                      }
                      onKeyDown={event => event.stopPropagation()}
                    />
                    {errors.batchDate && (
                      <Field.ErrorText>{errors.batchDate}</Field.ErrorText>
                    )}
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Data Reserva</Field.Label>
                    <Field.Input
                      type='datetime-local'
                      value={draft.reservedDate}
                      onChange={event =>
                        update('reservedDate', event.target.value)
                      }
                      onKeyDown={event => event.stopPropagation()}
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Data Uso</Field.Label>
                    <Field.Input
                      type='datetime-local'
                      value={draft.usedDate}
                      onChange={event => update('usedDate', event.target.value)}
                      onKeyDown={event => event.stopPropagation()}
                    />
                  </Field.Root>

                  <Field.Root>
                    <Field.Label>Usuário da reserva</Field.Label>
                    <Field.Input
                      value={draft.userName}
                      placeholder='—'
                      onChange={event => update('userName', event.target.value)}
                      onKeyDown={event => event.stopPropagation()}
                    />
                  </Field.Root>
                </div>

                {item.updatedAt && (
                  <p className='border-t border-alba-gray-200 pt-3 text-xs text-alba-gray-500'>
                    Última alteração em{' '}
                    {item.updatedAt.slice(0, 16).replace('T', ' ')}
                    {item.updatedByName ? ` por ${item.updatedByName}` : ''}.
                  </p>
                )}
              </>
            )}
          </Dialog.Body>

          <Dialog.Footer className='flex justify-end gap-2'>
            <Button variant='ghost' size='md' onClick={onClose}>
              Cancelar
            </Button>
            <Button
              variant='primary'
              size='md'
              onClick={handleSave}
              disabled={!hasChanges}
            >
              Salvar
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
}
